import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../utils/const";

const formatDisplayDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-IN");
};

const formatDisplayTime = (dateStr) => {
  if (!dateStr) return "-";
  // Prefer time part from "YYYY-MM-DD HH:mm:ss"
  const parts = String(dateStr).trim().split(/\s+/);
  if (parts.length >= 2) {
    return parts[1].slice(0, 8);
  }
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
};

const toDateStr = (year, month, day) => {
  const m = String(month + 1).padStart(2, "0");
  const d = String(day).padStart(2, "0");
  return `${year}-${m}-${d}`;
};

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const isWithdrawRow = (row) => {
  const type = String(row.transaction_type || row.type || "").toLowerCase();
  return type === "withdrawal" || type === "withdraw" || Number(row.amount) < 0;
};

const buildPrintHtml = ({ rows, totalAmount, schemeName, startDate, endDate }) => {
  const dateLabel =
    startDate === endDate
      ? `Date: ${formatDisplayDate(startDate)}`
      : `Date: ${formatDisplayDate(startDate)} - ${formatDisplayDate(endDate)}`;

  const tableRowsHtml =
    rows.length > 0
      ? rows
          .map((row) => {
            const amount = Math.abs(Number(row.amount || 0));
            const typeLabel = isWithdrawRow(row) ? "Withdraw" : "";
            return `
            <tr>
              <td>${row.card_no || "-"}</td>
              <td>${row.user_name || "-"}</td>
              <td style="text-align:right;">₹${amount.toLocaleString("en-IN")}</td>
              <td>${typeLabel}</td>
              <td>${row.payment_mode || "-"}</td>
              <td>${formatDisplayDate(row.payment_date) || "-"}</td>
              <td>${row.paid_by || "-"}</td>
              <td>${formatDisplayTime(row.created_on)}</td>
            </tr>`;
          })
          .join("")
      : `<tr><td colspan="8" style="text-align:center;">No transactions found</td></tr>`;

  return `<!doctype html>
    <html>
      <head>
        <title>Today Report</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 24px; color: #111; margin: 0; }
          .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 16px; }
          h2 { margin: 0; }
          .date { font-size: 14px; margin-top: 4px; }
          table { width: 100%; border-collapse: collapse; margin-top: 12px; }
          th, td { border: 1px solid #ddd; padding: 8px; font-size: 12px; }
          th { background: #f5f5f5; text-align: left; }
          .summary { margin-top: 12px; font-weight: bold; }
        </style>
      </head>
      <body>
        <div class="header">
          <h2>${schemeName} - Collection Report</h2>
          <div class="date">${dateLabel}</div>
        </div>
        <table>
          <thead>
            <tr>
              <th>Card No</th>
              <th>Member Name</th>
              <th>Amount</th>
              <th>Type</th>
              <th>Payment Mode</th>
              <th>Payment Date</th>
              <th>Paid By</th>
              <th>Time</th>
            </tr>
          </thead>
          <tbody>${tableRowsHtml}</tbody>
        </table>
        <div class="summary">Total: ₹${totalAmount.toLocaleString("en-IN")}</div>
      </body>
    </html>`;
};

const printInHiddenFrame = (html) => {
  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "none";
  iframe.style.visibility = "hidden";
  document.body.appendChild(iframe);

  const frameDoc = iframe.contentWindow?.document;
  if (!frameDoc) {
    document.body.removeChild(iframe);
    return false;
  }

  frameDoc.open();
  frameDoc.write(html);
  frameDoc.close();

  setTimeout(() => {
    iframe.contentWindow?.focus();
    iframe.contentWindow?.print();
    setTimeout(() => {
      if (iframe.parentNode) document.body.removeChild(iframe);
    }, 1000);
  }, 250);

  return true;
};

const TodayReport = () => {
  const navigate = useNavigate();
  const today = new Date();
  const todayStr = today.toISOString().split("T")[0];

  const [startDate, setStartDate] = useState(todayStr);
  const [endDate, setEndDate] = useState(todayStr);
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [schemeType, setSchemeType] = useState(
    () => localStorage.getItem("schemeType") || "mandal"
  );

  useEffect(() => {
    const handleSchemeChange = () => {
      setSchemeType(localStorage.getItem("schemeType") || "mandal");
    };
    window.addEventListener("schemeChanged", handleSchemeChange);
    return () => window.removeEventListener("schemeChanged", handleSchemeChange);
  }, []);

  const handleCancel = () => {
    navigate("/dashboard");
  };

  const handlePrevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((y) => y - 1);
    } else {
      setViewMonth((m) => m - 1);
    }
  };

  const handleCurrentMonth = () => {
    setViewYear(today.getFullYear());
    setViewMonth(today.getMonth());
  };

  const selectDay = (day) => {
    const clicked = toDateStr(viewYear, viewMonth, day);
    setError("");

    // Both already selected → start new range
    if (startDate && endDate) {
      setStartDate(clicked);
      setEndDate("");
      return;
    }

    // Only start selected → set end (auto-swap if needed)
    if (startDate && !endDate) {
      if (clicked < startDate) {
        setEndDate(startDate);
        setStartDate(clicked);
      } else {
        setEndDate(clicked);
      }
      return;
    }

    // Fallback
    setStartDate(clicked);
    setEndDate("");
  };

  const selectToday = () => {
    setStartDate(todayStr);
    setEndDate(todayStr);
    setViewYear(today.getFullYear());
    setViewMonth(today.getMonth());
    setError("");
  };

  const selectYesterday = () => {
    const y = new Date(today);
    y.setDate(y.getDate() - 1);
    const str = y.toISOString().split("T")[0];
    setStartDate(str);
    setEndDate(str);
    setViewYear(y.getFullYear());
    setViewMonth(y.getMonth());
    setError("");
  };

  const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDay = getFirstDayOfMonth(viewYear, viewMonth);

  const calendarCells = [];
  for (let i = 0; i < firstDay; i++) calendarCells.push(null);
  for (let d = 1; d <= daysInMonth; d++) calendarCells.push(d);

  const isInRange = (day) => {
    if (!startDate || !endDate) return false;
    const current = toDateStr(viewYear, viewMonth, day);
    return current > startDate && current < endDate;
  };

  const isEdge = (day) => {
    const current = toDateStr(viewYear, viewMonth, day);
    return current === startDate || current === endDate;
  };

  const handlePrint = async () => {
    if (!startDate || !endDate) {
      setError("Please select start date and end date.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const apiUrl =
        schemeType === "kanchan"
          ? "/kanchan/today_report.php"
          : "/mandal/today_report.php";

      const res = await apiClient.post(apiUrl, {
        start_date: startDate,
        end_date: endDate,
      });

      if (!res?.data?.status || !Array.isArray(res?.data?.data)) {
        setError(res?.data?.message || "Unable to load report for printing.");
        setLoading(false);
        return;
      }

      const rows = res.data.data;
      const totalAmount = Number(res.data.total_amount || 0);
      const safeSchemeName = schemeType === "kanchan" ? "Kanchanvarsha" : "Mandal";

      const printHtml = buildPrintHtml({
        rows,
        totalAmount,
        schemeName: safeSchemeName,
        startDate,
        endDate,
      });

      const printed = printInHiddenFrame(printHtml);
      if (!printed) {
        setError("Unable to open print preview.");
      }
    } catch (err) {
      setError("Something went wrong while generating the report.");
    } finally {
      setLoading(false);
    }
  };

  const rangeLabel =
    startDate && endDate
      ? startDate === endDate
        ? formatDisplayDate(startDate)
        : `${formatDisplayDate(startDate)} - ${formatDisplayDate(endDate)}`
      : startDate
      ? `${formatDisplayDate(startDate)} - Select end date`
      : "Select date range";

  return (
    <div className="page-wrapper">
      <div className="page-content">
        <div className="container-xxl" />
      </div>

      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          background: "rgba(0, 0, 0, 0.45)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 9999,
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: 620,
            background: "#fff",
            borderRadius: 16,
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.25)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "18px 24px",
              borderBottom: "1px solid #eee",
            }}
          >
            <div>
              <h5 style={{ margin: 0, fontWeight: 700, color: "#1a1d29" }}>
                Select Collection Date
              </h5>
              <p style={{ margin: "4px 0 0", fontSize: "0.85rem", color: "#6b7280" }}>
                {rangeLabel}
              </p>
            </div>
            <button
              onClick={handleCancel}
              style={{
                background: "none",
                border: "none",
                fontSize: "1.4rem",
                cursor: "pointer",
                color: "#666",
                lineHeight: 1,
              }}
            >
              &times;
            </button>
          </div>

          <div style={{ padding: "20px 24px", display: "flex", gap: 20 }}>
            <div
              style={{
                flex: 1,
                border: "1px solid #e5e7eb",
                borderRadius: 12,
                padding: 16,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 12,
                }}
              >
                <button
                  onClick={handlePrevMonth}
                  style={{
                    border: "1px solid #d1d5db",
                    background: "#fff",
                    borderRadius: 6,
                    padding: "4px 12px",
                    cursor: "pointer",
                    fontSize: "0.85rem",
                  }}
                >
                  &larr; Previous
                </button>
                <span style={{ fontWeight: 700, fontSize: "1rem" }}>
                  {MONTHS[viewMonth]} {viewYear}
                </span>
                <button
                  onClick={handleCurrentMonth}
                  style={{
                    border: "1px solid #d1d5db",
                    background: "#fff",
                    borderRadius: 6,
                    padding: "4px 12px",
                    cursor: "pointer",
                    fontSize: "0.85rem",
                  }}
                >
                  Current
                </button>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(7, 1fr)",
                  textAlign: "center",
                  marginBottom: 6,
                }}
              >
                {DAYS.map((d) => (
                  <div
                    key={d}
                    style={{
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      color: "#6b7280",
                      padding: "4px 0",
                    }}
                  >
                    {d}
                  </div>
                ))}
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(7, 1fr)",
                  gap: 4,
                }}
              >
                {calendarCells.map((day, i) => {
                  if (day === null) return <div key={`e${i}`} />;
                  const edge = isEdge(day);
                  const inRange = isInRange(day);
                  const isTodayCell =
                    today.getFullYear() === viewYear &&
                    today.getMonth() === viewMonth &&
                    today.getDate() === day;
                  return (
                    <button
                      key={day}
                      onClick={() => selectDay(day)}
                      style={{
                        width: "100%",
                        aspectRatio: "1",
                        border: edge || inRange ? "none" : "1px solid #e5e7eb",
                        borderRadius: 8,
                        background: edge
                          ? "#6b1c2e"
                          : inRange
                          ? "rgba(107, 28, 46, 0.18)"
                          : "#fff",
                        color: edge
                          ? "#fff"
                          : isTodayCell
                          ? "#6b1c2e"
                          : "#111",
                        fontWeight: edge || isTodayCell ? 700 : 400,
                        cursor: "pointer",
                        fontSize: "0.9rem",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {day}
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 12, minWidth: 120 }}>
              <button
                onClick={selectToday}
                style={{
                  border: "1px solid #d1d5db",
                  background: "#fff",
                  borderRadius: 10,
                  padding: "14px 20px",
                  cursor: "pointer",
                  fontWeight: 600,
                  fontSize: "0.95rem",
                }}
              >
                Today
              </button>
              <button
                onClick={selectYesterday}
                style={{
                  border: "1px solid #d1d5db",
                  background: "#fff",
                  borderRadius: 10,
                  padding: "14px 20px",
                  cursor: "pointer",
                  fontWeight: 600,
                  fontSize: "0.95rem",
                }}
              >
                Yesterday
              </button>
            </div>
          </div>

          {error && (
            <div style={{ padding: "0 24px" }}>
              <div className="alert alert-danger py-2 mb-0">{error}</div>
            </div>
          )}

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 12,
              padding: "16px 24px",
              borderTop: "1px solid #eee",
              background: "#f9fafb",
            }}
          >
            <button
              type="button"
              onClick={handleCancel}
              disabled={loading}
              style={{
                border: "1px solid #d1d5db",
                background: "#fff",
                borderRadius: 8,
                padding: "10px 24px",
                cursor: "pointer",
                fontWeight: 500,
                fontSize: "0.95rem",
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handlePrint}
              disabled={loading || !startDate || !endDate}
              style={{
                border: "none",
                background: "#6b1c2e",
                color: "#fff",
                borderRadius: 8,
                padding: "10px 28px",
                cursor: loading || !startDate || !endDate ? "not-allowed" : "pointer",
                fontWeight: 600,
                fontSize: "0.95rem",
                opacity: loading || !startDate || !endDate ? 0.6 : 1,
              }}
            >
              {loading ? "Generating..." : "Print"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TodayReport;
